import {
  all,
  call,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { isEmpty } from 'lodash';

import {
  chooseDocumentCancellation,
  chooseDocumentFailure,
  chooseDocumentSuccess,
  downloadAttachmentFailure,
  fetchAttachmentCancelled,
  fetchAttachmentFailure,
  fetchAttachmentSuccess,
  fetchMessageDetailsFailure,
  fetchMessageDetailsSuccess,
  fetchMessagesFailure,
  fetchMessagesSuccess,
  postMessageFailure,
  postMessageSuccess,
  postReplyFailure,
  postReplySuccess,
  processDocument,
  processDocumentFailure,
  processDocumentSuccess,
  setAttachmentFile,
  updateAttachments,
  uploadAttachmentsFailure,
} from './actions';
import {
  ATTACHMENT_BLOCKED_MESSAGE,
  CHOOSE_DOCUMENT,
  DOWNLOAD_ATTACHMENT,
  DOWNLOAD_ERROR_MESSAGE,
  FETCH_ATTACHMENT,
  FETCH_MESSAGES,
  FETCH_MESSAGES_FAILURE_MESSAGE,
  FETCH_MESSAGE_DETAILS,
  FETCH_MESSAGE_DETAILS_FAILURE_MESSAGE,
  POST_MESSAGE,
  POST_MESSAGE_FAILURE_ERROR_MESSAGE,
  POST_REPLY,
  PROCESS_DOCUMENT,
} from './constants';
import { chooseFileSaga, processFileSaga } from '../../utils/docUpload';
import { get, post } from '../../utils/request';
import {
  isMaxFileSizeError,
  parseDocUploadAttachmentBackendErrors,
  parseDocUploadAttachmentFrontendErrors,
  triggerDownloadBlob,
} from '../../utils/globalHelpers';
import makeSelectMain from '../Main/selectors';
import makeSelectMobile from '../Mobile/selectors';
import makeSelectSecureMessaging from './selectors';
import MobileDeviceHelpers from '../../utils/MobileDeviceHelpers';

export function* fetchMessageDetailsSaga({ baseUrl }, { payload: { messageId } }) {
  try {
    const endpoint = `${baseUrl}/securemessaging/message/${messageId}`;
    const { messageDetails } = yield call(get, endpoint);
    yield put(fetchMessageDetailsSuccess({ messageDetails }));
  } catch (error) {
    yield put(fetchMessageDetailsFailure({ error: FETCH_MESSAGE_DETAILS_FAILURE_MESSAGE }));
  }
}

export function* downloadAttachmentSaga({ baseUrl }, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    const messageId = yield select(makeSelectSecureMessaging('selectedMessageId'));
    const endpoint = `${baseUrl}/securemessaging/message/attachment/download`;
    const { blob, fileName } = yield call(post, endpoint, { attachment: payload, messageId });
    if (!isMobile) {
      yield call(triggerDownloadBlob, blob, fileName);
    }
  } catch {
    yield put(downloadAttachmentFailure(DOWNLOAD_ERROR_MESSAGE));
  }
}

export function* fetchAttachmentSaga({ baseUrl }, { payload }) {
  try {
    const messageId = yield select(makeSelectSecureMessaging('selectedMessageId'));
    const { attachment, dispatchSetFile, dispatchToggleDrawer } = payload;
    const endpoint = `${baseUrl}/securemessaging/message/attachment/fetch`;
    const {
      error,
      fileContent,
      fileName,
      fileType,
    } = yield call(post, endpoint, { attachment, messageId });
    if (error) {
      yield put(fetchAttachmentFailure({ error }));
    } else {
      const isMobile = yield select(makeSelectMobile('isMobile'));
      if (isMobile) {
        const errorMessage = yield call(MobileDeviceHelpers.saveAttachmentToFile, {
          dispatchSetFile,
          dispatchToggleDrawer,
          fileContent,
          fileName,
          fileType,
        });
        if (errorMessage) {
          const isNativeMobile = yield select(makeSelectMobile('isNativeMobile'));
          if (isNativeMobile) {
          /** if a user hits back before choosing a download location an error will be thrown,
          * in this case we don't want to display any banner error just return to statement list
          * it seems like this is only possible on Android, but this should cover iOS as well */
            yield put(fetchAttachmentCancelled());
          } else {
            /** If mobile web and an error is thrown - it will be due to pop ups being blocked,
           * thus we want to display a banner error describing the problem */
            yield put(fetchAttachmentFailure(ATTACHMENT_BLOCKED_MESSAGE));
          }
        }
      } else {
        yield put(setAttachmentFile({ fileName, uri: fileContent }));
      }
      yield put(fetchAttachmentSuccess());
    }
  } catch {
    yield put(fetchAttachmentFailure(DOWNLOAD_ERROR_MESSAGE));
  }
}

export function* fetchMessagesSaga({ baseUrl }) {
  try {
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/securemessaging/messages/${loanNumber}`;
    const { messages } = yield call(get, endpoint);
    yield put(fetchMessagesSuccess({ messages }));
  } catch (error) {
    yield put(fetchMessagesFailure({ error: FETCH_MESSAGES_FAILURE_MESSAGE }));
  }
}

export function* postMessageSaga({ baseUrl }, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    // Update form attachments in redux before call.
    // Mobile handles this in the component level
    if (!isMobile) {
      yield put(updateAttachments({ isSubmit: true }));
    }
    const loanNumber = yield select(makeSelectMain('selectedLoan'));
    const endpoint = `${baseUrl}/securemessaging/message/${loanNumber}`;
    const { error, message, ...restResult } = yield call(post, endpoint, payload);
    if (error) {
      const { reasons } = error;
      const { failedAttachmentsIndices } = restResult;

      const attachmentErrors = parseDocUploadAttachmentBackendErrors({
        failedAttachmentsIndices,
        reasons,
      });
      if (attachmentErrors.length > 0) {
        yield put(uploadAttachmentsFailure({ attachmentErrors }));
      } else {
        throw new Error();
      }
    } else {
      const { errors, messageId } = message;
      if (isEmpty(errors)) {
        yield put(postMessageSuccess({ messageId }));
      } else {
        yield put(postMessageFailure({ error: POST_MESSAGE_FAILURE_ERROR_MESSAGE }));
      }
    }
  } catch (error) {
    if (isMaxFileSizeError(error)) {
      const { attachments } = payload;
      const attachmentErrors = parseDocUploadAttachmentFrontendErrors({ attachments });
      yield put(uploadAttachmentsFailure({ attachmentErrors }));
    } else {
      yield put(postMessageFailure({ error: POST_MESSAGE_FAILURE_ERROR_MESSAGE }));
    }
  }
}

export function* postReplySaga({ baseUrl }, {
  payload: {
    attachments,
    body,
    messageId,
  },
}) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    // Update form attachments in redux before call.
    // Mobile handles this in the component level
    if (!isMobile) {
      yield put(updateAttachments({ isSubmit: true }));
    }
    const endpoint = `${baseUrl}/securemessaging/reply/${messageId}`;
    const requestBody = { attachments, text: body };
    const { error, reply, ...restResult } = yield call(post, endpoint, requestBody);
    if (error) {
      const { reasons } = error;
      const { failedAttachmentsIndices } = restResult;

      const attachmentErrors = parseDocUploadAttachmentBackendErrors({
        failedAttachmentsIndices,
        reasons,
      });
      if (attachmentErrors.length > 0) {
        yield put(uploadAttachmentsFailure({
          attachmentErrors,
          isMobile,
          isReply: true,
        }));
      } else {
        throw new Error();
      }
    } else {
      yield put(postReplySuccess({ reply }));
    }
  } catch (error) {
    const isMobile = yield select(makeSelectMobile('isMobile'));
    if (isMaxFileSizeError(error)) {
      const attachmentErrors = parseDocUploadAttachmentFrontendErrors({ attachments });
      yield put(uploadAttachmentsFailure({
        attachmentErrors,
        isMobile,
        isReply: true,
      }));
    } else {
      yield put(postReplyFailure({ error: POST_MESSAGE_FAILURE_ERROR_MESSAGE }));
    }
  }
}

export default (options) => function* rootSaga() {
  yield all([
    takeLatest(
      CHOOSE_DOCUMENT,
      chooseFileSaga,
      {
        ...options,
        chooseDocumentCancellation,
        chooseDocumentFailure,
        chooseDocumentSuccess,
        processDocument,
      },
    ),
    takeLatest(DOWNLOAD_ATTACHMENT, downloadAttachmentSaga, options),
    takeLatest(FETCH_ATTACHMENT, fetchAttachmentSaga, options),
    takeLatest(FETCH_MESSAGE_DETAILS, fetchMessageDetailsSaga, options),
    takeLatest(FETCH_MESSAGES, fetchMessagesSaga, options),
    takeLeading(POST_MESSAGE, postMessageSaga, options),
    takeLeading(POST_REPLY, postReplySaga, options),
    takeLatest(
      PROCESS_DOCUMENT,
      processFileSaga,
      {
        ...options,
        processDocumentFailure,
        processDocumentSuccess,
      },
    ),
  ]);
};
